import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { api } from '@/lib/api'
import { BULK_WEBHOOK_EVENTS } from '@/lib/bulkEvents'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { CopyButton } from '@/components/shared/CopyButton'
import { EmptyState } from '@/components/shared/EmptyState'
import type { Webhook } from '@/types'
import { Webhook as WebhookIcon, Plus, Trash2, Loader2, Activity, X, ChevronLeft, ChevronRight, Edit3, Send, CheckCircle2, XCircle } from 'lucide-react'

const WEBHOOK_EVENTS = [
  { value: 'all', label: 'All events' },
  ...BULK_WEBHOOK_EVENTS,
  { value: 'inbound_email', label: 'Inbound email' },
  { value: 'delivery_status', label: 'Delivery status' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'domain_verified', label: 'Domain verified' },
  { value: 'mailbox_created', label: 'Mailbox created' },
  { value: 'migration_complete', label: 'Migration complete' },
  { value: 'rate_limit_warning', label: 'Rate limit warning' },
]

function normalizeIps(raw: string) {
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

export function Webhooks() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  const [addOpen, setAddOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<any>(null)
  const [form, setForm] = useState({
    url: '',
    events: ['inbound_email'] as string[],
    timeout_seconds: 30,
    allowed_ips: '',
  })

  const [logOpen, setLogOpen] = useState(false)
  const [deliveryLog, setDeliveryLog] = useState<any[]>([])
  const [logLoading, setLogLoading] = useState(false)
  const [expandedDelivery, setExpandedDelivery] = useState<string | null>(null)

  const [editOpen, setEditOpen] = useState(false)
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null)
  const [editForm, setEditForm] = useState({
    url: '',
    events: [] as string[],
    active: true,
    timeout_seconds: 30,
    allowed_ips: '',
  })
  const [savingEdit, setSavingEdit] = useState(false)

  const [testOpen, setTestOpen] = useState(false)
  const [testResult, setTestResult] = useState<any>(null)
  const [testing, setTesting] = useState(false)

  const fetchWebhooks = async () => {
    setLoading(true)
    try {
      const res = await api.get(`/webhooks?page=${page}&per_page=20`)
      setWebhooks(res.data.webhooks || [])
      setTotal(res.data.pagination?.total || 0)
      setTotalPages(res.data.pagination?.total_pages || 1)
    } catch {
      toast.error('Failed to load webhooks')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWebhooks()
  }, [page])

  const toggleEvent = (
    ev: string,
    current: string[],
    setter: (events: string[]) => void
  ) => {
    if (ev === 'all') {
      setter(current.includes('all') ? [] : ['all'])
      return
    }
    const withoutAll = current.filter((e) => e !== 'all')
    if (withoutAll.includes(ev)) {
      setter(withoutAll.filter((e) => e !== ev))
    } else {
      setter([...withoutAll, ev])
    }
  }

  const handleCreate = async () => {
    if (!form.url) return
    setCreating(true)
    try {
      const res = await api.post('/webhooks', {
        ...form,
        allowed_ips: normalizeIps(form.allowed_ips),
      })
      setCreated(res.data)
      fetchWebhooks()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to create webhook')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this webhook?')) return
    try {
      await api.delete(`/webhooks/${id}`)
      fetchWebhooks()
    } catch {
      toast.error('Failed to delete')
    }
  }

  const viewLog = async (id: string) => {
    setLogOpen(true)
    setLogLoading(true)
    try {
      const res = await api.get(`/webhooks/${id}/deliveries`)
      setDeliveryLog(res.data.deliveries || [])
    } catch {
      setDeliveryLog([])
    } finally {
      setLogLoading(false)
    }
  }

  const openEdit = (wh: Webhook) => {
    setEditingWebhook(wh)
    setEditForm({
      url: wh.url,
      events: wh.events,
      active: wh.active,
      timeout_seconds: wh.timeout_seconds,
      allowed_ips: (wh.allowed_ips || []).join('\n'),
    })
    setEditOpen(true)
  }

  const handleEditSave = async () => {
    if (!editingWebhook) return
    setSavingEdit(true)
    try {
      await api.put(`/webhooks/${editingWebhook.id}`, {
        ...editForm,
        allowed_ips: normalizeIps(editForm.allowed_ips),
      })
      toast.success('Webhook updated')
      setEditOpen(false)
      fetchWebhooks()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to update webhook')
    } finally {
      setSavingEdit(false)
    }
  }

  const handleTest = async (id: string) => {
    setTesting(true)
    setTestOpen(true)
    setTestResult(null)
    try {
      const res = await api.post(`/webhooks/${id}/test`)
      setTestResult(res.data)
    } catch (err: any) {
      setTestResult({ success: false, error: err.response?.data?.detail?.message || 'Test failed' })
    } finally {
      setTesting(false)
    }
  }

  const resetAddDialog = (open: boolean) => {
    setAddOpen(open)
    if (!open) {
      setCreated(null)
      setForm({ url: '', events: ['inbound_email'], timeout_seconds: 30, allowed_ips: '' })
    }
  }

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-32 bg-accent rounded" />
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Webhooks</h1>
          <p className="text-sm text-muted-foreground mt-1">Send inbound mail and delivery events to your application.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700">
          <Plus className="mr-2 h-4 w-4" />
          Add Webhook
        </Button>
      </div>

      {webhooks.length === 0 ? (
        <EmptyState
          title="No webhooks yet"
          description="Create a webhook to receive inbound mail or delivery events."
          icon={<WebhookIcon className="h-5 w-5" />}
          action={<Button onClick={() => setAddOpen(true)}><Plus className="mr-2 h-4 w-4" />Add Webhook</Button>}
        />
      ) : (
        <>
          <div className="grid gap-3">
            {webhooks.map((wh) => (
              <Card key={wh.id} className="hover:bg-accent/20 transition-colors">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
                      <WebhookIcon className="h-5 w-5 text-pink-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold truncate">{wh.url}</p>
                        <StatusBadge status={wh.active} />
                        {wh.failure_count > 0 && (
                          <span className="text-[10px] text-amber-400">{wh.failure_count} failures</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-xs text-muted-foreground">{wh.events.join(', ')}</span>
                        <span className="text-[10px] text-muted-foreground">• {wh.timeout_seconds}s timeout</span>
                        {wh.allowed_ips && wh.allowed_ips.length > 0 && (
                          <span className="text-[10px] text-muted-foreground">• {wh.allowed_ips.length} IP rule(s)</span>
                        )}
                        {wh.last_success_at && (
                          <span className="text-[10px] text-emerald-400">Last success: {new Date(wh.last_success_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleTest(wh.id)} title="Test webhook">
                      <Send className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(wh)} title="Edit webhook">
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => viewLog(wh.id)} title="View delivery log">
                      <Activity className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(wh.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{total} total</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">Page {page}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Test Result Dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Webhook Test Result</DialogTitle>
          </DialogHeader>
          {testing ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
            </div>
          ) : testResult ? (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                    <span className="text-sm font-medium text-emerald-400">Delivered successfully</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-rose-400" />
                    <span className="text-sm font-medium text-rose-400">Delivery failed</span>
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-accent/30 rounded-lg px-3 py-2">
                  <p className="text-muted-foreground text-xs">Status Code</p>
                  <p className="font-semibold">{testResult.status_code ?? '-'}</p>
                </div>
                <div className="bg-accent/30 rounded-lg px-3 py-2">
                  <p className="text-muted-foreground text-xs">Response Time</p>
                  <p className="font-semibold">{testResult.response_time_ms ?? 0} ms</p>
                </div>
              </div>
              {testResult.response_preview && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Response preview</p>
                  <div className="rounded-lg bg-accent/50 p-2 text-xs font-mono break-all max-h-32 overflow-y-auto">
                    {testResult.response_preview}
                  </div>
                </div>
              )}
              {testResult.error && (
                <p className="text-sm text-destructive">{testResult.error}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No test result.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Delivery Log Dialog */}
      <Dialog open={logOpen} onOpenChange={(open) => { setLogOpen(open); if (!open) setExpandedDelivery(null) }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Delivery Log</DialogTitle>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setLogOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          {logLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
            </div>
          ) : deliveryLog.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No delivery attempts yet.</p>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-accent/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Event</th>
                    <th className="px-3 py-2 text-left font-medium">Attempt</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                    <th className="px-3 py-2 text-left font-medium">Time</th>
                    <th className="px-3 py-2 text-left font-medium">Response</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryLog.map((entry: any) => (
                    <>
                      <tr key={entry.id} className="border-t">
                        <td className="px-3 py-2">
                          <p className="font-medium">{entry.event_type}</p>
                          <p className="text-[10px] text-muted-foreground truncate max-w-[140px]" title={entry.event_id}>{entry.event_id}</p>
                        </td>
                        <td className="px-3 py-2">#{entry.attempt_number}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded ${entry.success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>
                            {entry.status_code ?? '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{entry.response_time_ms ?? '—'} ms</td>
                        <td className="px-3 py-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-[10px]"
                            onClick={() => setExpandedDelivery(expandedDelivery === entry.id ? null : entry.id)}
                          >
                            {expandedDelivery === entry.id ? 'Hide' : 'Details'}
                          </Button>
                        </td>
                      </tr>
                      {expandedDelivery === entry.id && (
                        <tr className="border-t bg-accent/30">
                          <td colSpan={5} className="px-3 py-2">
                            <div className="space-y-2 text-xs">
                              <div>
                                <span className="text-muted-foreground">Event ID:</span>{' '}
                                <span className="font-mono">{entry.event_id}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Timestamp:</span>{' '}
                                {entry.created_at ? new Date(entry.created_at).toLocaleString() : '—'}
                              </div>
                              {entry.error_message && (
                                <div className="rounded bg-rose-500/10 p-2 text-rose-400 break-all">
                                  <span className="font-medium">Error:</span> {entry.error_message}
                                </div>
                              )}
                              {entry.response_body && (
                                <pre className="rounded bg-accent/50 p-2 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                                  {entry.response_body}
                                </pre>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Webhook</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="edit-url">URL</Label>
              <Input id="edit-url" value={editForm.url} onChange={(e) => setEditForm({ ...editForm, url: e.target.value })} />
            </div>
            <div>
              <Label>Events</Label>
              <div className="grid gap-2 mt-1.5 sm:grid-cols-2">
                {WEBHOOK_EVENTS.map((ev) => (
                  <div key={ev.value} className="flex items-center gap-1.5">
                    <Checkbox
                      id={`edit-event-${ev.value}`}
                      checked={editForm.events.includes(ev.value)}
                      onCheckedChange={() => toggleEvent(ev.value, editForm.events, (events) => setEditForm({ ...editForm, events }))}
                    />
                    <Label htmlFor={`edit-event-${ev.value}`} className="text-sm font-normal">{ev.label}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="edit-timeout">Timeout (seconds)</Label>
              <Input
                id="edit-timeout"
                type="number"
                min={5}
                max={60}
                value={editForm.timeout_seconds}
                onChange={(e) => setEditForm({ ...editForm, timeout_seconds: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="edit-allowed-ips">IP allowlist</Label>
              <Textarea
                id="edit-allowed-ips"
                rows={3}
                className="mt-1.5"
                placeholder="Leave blank to allow any IP&#10;PurpleToad Mail outbound IPs..."
                value={editForm.allowed_ips}
                onChange={(e) => setEditForm({ ...editForm, allowed_ips: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">One IP or CIDR per line or comma-separated.</p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="edit-active"
                checked={editForm.active}
                onCheckedChange={(checked) => setEditForm({ ...editForm, active: checked === true })}
              />
              <Label htmlFor="edit-active" className="text-sm font-normal">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={savingEdit || !editForm.url}>
              {savingEdit && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={resetAddDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Webhook</DialogTitle>
          </DialogHeader>
          {!created ? (
            <div className="space-y-4 py-2">
              {/* CHECKPOINT: PRD-10 FR-10.6.2 — Webhook config form with events, timeout, IP allowlist. */}
              <div>
                <Label htmlFor="webhook-url">URL</Label>
                <Input id="webhook-url" placeholder="https://your-app.com/webhook" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
              </div>
              <div>
                <Label>Events</Label>
                <div className="grid gap-2 mt-1.5 sm:grid-cols-2">
                  {WEBHOOK_EVENTS.map((ev) => (
                    <div key={ev.value} className="flex items-center gap-1.5">
                      <Checkbox
                        id={`event-${ev.value}`}
                        checked={form.events.includes(ev.value)}
                        onCheckedChange={() => toggleEvent(ev.value, form.events, (events) => setForm({ ...form, events }))}
                      />
                      <Label htmlFor={`event-${ev.value}`} className="text-sm font-normal">{ev.label}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="webhook-timeout">Timeout (seconds)</Label>
                <Input
                  id="webhook-timeout"
                  type="number"
                  min={5}
                  max={60}
                  value={form.timeout_seconds}
                  onChange={(e) => setForm({ ...form, timeout_seconds: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="webhook-allowed-ips">IP allowlist</Label>
                <Textarea
                  id="webhook-allowed-ips"
                  rows={3}
                  className="mt-1.5"
                  placeholder="Leave blank to allow any IP&#10;PurpleToad Mail outbound IPs..."
                  value={form.allowed_ips}
                  onChange={(e) => setForm({ ...form, allowed_ips: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">One IP or CIDR per line or comma-separated.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
                <p className="text-sm text-emerald-400 font-medium">Webhook created!</p>
                <p className="text-xs text-muted-foreground mt-1">Copy the secret now — it will not be shown again.</p>
              </div>
              <div className="bg-accent/50 rounded-lg px-3 py-2 flex items-center justify-between gap-3">
                <span className="text-xs font-mono break-all">{created.secret}</span>
                <CopyButton text={created.secret} />
              </div>
            </div>
          )}
          <DialogFooter>
            {!created ? (
              <>
                <Button variant="outline" onClick={() => resetAddDialog(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating || !form.url}>
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Add Webhook
                </Button>
              </>
            ) : (
              <Button onClick={() => resetAddDialog(false)}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
